import React from 'react'
import PropTypes from 'prop-types'
import {defineConfig, createSystem, ChakraProvider, defaultBaseConfig, Box} from '@chakra-ui/react3'

import {animationStyles} from './animation-styles'
import {breakpoints} from './breakpoints'
import {globalCss} from './global-css'
import {keyframes} from './keyframes'
import {layerStyles} from './layer-styles'
import {recipes} from './recipes'
import {semanticTokens} from './semantic-tokens'
import {slotRecipes} from './slot-recipes'
import {textStyles} from './text-styles'
import {tokens} from './tokens'

const config = defineConfig({
    cssVarsPrefix: 'leap',
    cssVarsRoot: ':where(html, .chakra-theme)',
    disableLayers: true, // required to allow recipes to work while chakra2 is also used
    preflight: true,
    conditions: {
        icon: '& :where(svg), & :where(.icon)', // use .icon for non-svg icons
        light: '.chakra-theme[data-theme=light] &',
        dark: '.chakra-theme[data-theme=dark] &',
        hasLeftIcon: '&:has(> svg:first-child, > .icon:first-child)', // use .icon for non-svg icons
        hasRightIcon: '&:has(> svg:last-child, > .icon:last-child)', // use .icon for non-svg icons
        hasOnlyIcon: '&:has(> svg:only-child, > .icon:only-child)' // use .icon for non-svg icons
    },
    globalCss,
    theme: {
        breakpoints,
        keyframes,
        tokens,
        semanticTokens,
        recipes,
        slotRecipes,
        textStyles,
        layerStyles,
        animationStyles
    }
})
const system = createSystem(defaultBaseConfig, config)

// an override to the default config that only uses mobile breakpoints for use in
// small containers like drawer
const mobileOnlyConfig = defineConfig({
    ...config,
    preflight: false,
    globalCss: false,
    theme: {
        ...config.theme,
        breakpoints: {
            ...Object.fromEntries(
                Object.keys(config.theme.breakpoints).map((key) => [key, '99999em'])
            ),
            base: '0px',
            xxs: '375px'
        }
    }
})
const mobileOnlySystem = createSystem(defaultBaseConfig, mobileOnlyConfig)

/**
 * Theme wrapper for Chakra v3 sections.
 *
 * Applies the theme through the `chakra-theme` class and `data-theme` attribute,
 * allowing nested content to be rendered in either light or dark mode.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} props.children - Content to render inside the themed wrapper.
 * @param {'light'|'dark'} [props.theme='light'] - Theme variant applied to the wrapper.
 * @returns {JSX.Element} The themed wrapper element.
 */
export const Chakra3Theme = ({children, theme = 'light'}) => {
    return (
        <Box className="chakra-theme" data-theme={theme} display="contents">
            {children}
        </Box>
    )
}

Chakra3Theme.propTypes = {
    children: PropTypes.node,
    theme: PropTypes.oneOf(['light', 'dark'])
}

const Chakra3Provider = ({children, theme = 'light'}) => {
    return (
        <ChakraProvider value={system}>
            <Chakra3Theme theme={theme}>{children}</Chakra3Theme>
        </ChakraProvider>
    )
}

Chakra3Provider.propTypes = {
    children: PropTypes.node,
    theme: PropTypes.oneOf(['light', 'dark'])
}

export const Chakra3MobileOnlyProvider = ({children}) => {
    return <ChakraProvider value={mobileOnlySystem}>{children}</ChakraProvider>
}

Chakra3MobileOnlyProvider.propTypes = {
    children: PropTypes.node
}

export default Chakra3Provider
